(() => {
  const ROOT_SELECTOR = "[data-gdp-pdp-engine]";

  const collectRoots = scope => {
    const roots = [];
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(root => roots.push(root));
    return roots;
  };

  const initRoot = root => {
    if (root.dataset.gdpPdpReady === "true") return;
    root.dataset.gdpPdpReady = "true";

    const cleanups = [];
    const reviewWidgetSelector = "#trustoo-widget-wrapper, #tt-carousels-wrapper, #tt-carousel, #reviews-wrapper, #noreviews-wrapper, #vstar-noreviews, .trustoo-reviews-wrapper";
    const hideUnverifiedReviewWidget = widget => {
      if (!(widget instanceof Element)) return;
      if (widget.closest(".gdp-jv3f__reviews, .gdp-pdp-engine__reviews")) return;
      widget.hidden = true;
      widget.setAttribute("aria-hidden", "true");
      widget.style.setProperty("display", "none", "important");
      widget.style.setProperty("min-height", "0", "important");
      widget.style.setProperty("height", "0", "important");
    };
    const suppressUnverifiedReviewCards = (scope = document) => {
      if (scope instanceof Element && scope.matches(reviewWidgetSelector)) hideUnverifiedReviewWidget(scope);
      scope.querySelectorAll?.(reviewWidgetSelector).forEach(hideUnverifiedReviewWidget);
    };
    suppressUnverifiedReviewCards(document);
    let reviewGuardFrame = 0;
    const reviewGuardQueue = new Set();
    const unverifiedReviewsObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => mutation.addedNodes.forEach(node => {
        if (node instanceof Element) reviewGuardQueue.add(node);
      }));
      if (reviewGuardFrame || !reviewGuardQueue.size) return;
      reviewGuardFrame = window.requestAnimationFrame(() => {
        reviewGuardFrame = 0;
        reviewGuardQueue.forEach(suppressUnverifiedReviewCards);
        reviewGuardQueue.clear();
      });
    });
    unverifiedReviewsObserver.observe(document.body, { childList: true, subtree: true });
    const reviewGuardTimeout = window.setTimeout(() => unverifiedReviewsObserver.disconnect(), 12000);
    cleanups.push(() => {
      unverifiedReviewsObserver.disconnect();
      window.clearTimeout(reviewGuardTimeout);
      window.cancelAnimationFrame(reviewGuardFrame);
      reviewGuardQueue.clear();
    });
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    let reducedMotion = motionQuery.matches;
    const onMotionPreferenceChange = event => {
      reducedMotion = event.matches;
    };

    if (typeof motionQuery.addEventListener === "function") {
      motionQuery.addEventListener("change", onMotionPreferenceChange);
      cleanups.push(() => motionQuery.removeEventListener("change", onMotionPreferenceChange));
    } else if (typeof motionQuery.addListener === "function") {
      motionQuery.addListener(onMotionPreferenceChange);
      cleanups.push(() => motionQuery.removeListener(onMotionPreferenceChange));
    }

    const hero = root.querySelector("[data-gdp-pdp-hero]");
    const track = root.querySelector("[data-gdp-pdp-hero-track]");
    const slides = Array.from(root.querySelectorAll("[data-gdp-pdp-slide]"));
    const count = root.querySelector("[data-gdp-pdp-count]");
    const previous = root.querySelector("[data-gdp-pdp-prev]");
    const next = root.querySelector("[data-gdp-pdp-next]");
    let activeSlide = 0;
    let paintedSlide = -1;
    let galleryFrame = 0;
    let requestedSlide = null;
    let gallerySettleTimer = 0;
    let galleryTouchActive = false;
    const galleryRatios = slides.map(slide => Number(slide.dataset.gdpImageRatio))
      .filter(ratio => Number.isFinite(ratio) && ratio > 0);
    if (hero && galleryRatios.length) {
      hero.style.setProperty("--gdp-gallery-nav-ratio", String(Math.max(...galleryRatios)));
    }
    const commitGalleryRatio = index => {
      const imageRatio = Number(slides[index]?.dataset.gdpImageRatio);
      if (hero && Number.isFinite(imageRatio) && imageRatio > 0) {
        hero.style.setProperty("--gdp-active-image-ratio", String(imageRatio));
      }
    };

    const warmGalleryImage = index => {
      const image = slides[index]?.querySelector('img[loading="lazy"]');
      if (!image) return;
      image.loading = "eager";
      image.fetchPriority = "low";
      image.decode?.().catch(() => {});
    };

    const paintGallery = index => {
      if (!slides.length) return;
      activeSlide = Math.max(0, Math.min(slides.length - 1, index));
      if (paintedSlide === activeSlide) return;
      paintedSlide = activeSlide;
      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === activeSlide;
        slide.classList.toggle("is-active", isActive);
        slide.setAttribute("aria-hidden", String(!isActive));
        if (isActive) {
          slide.setAttribute("aria-current", "true");
          slide.removeAttribute("inert");
        } else {
          slide.removeAttribute("aria-current");
          slide.setAttribute("inert", "");
        }
      });
      if (count) {
        count.textContent = `${String(activeSlide + 1).padStart(2, "0")} / ${String(slides.length).padStart(2, "0")}`;
      }
      hero?.style.setProperty("--gdp-gallery-index", String(activeSlide));
      if (previous) {
        previous.disabled = activeSlide === 0;
        previous.setAttribute("aria-disabled", String(previous.disabled));
      }
      if (next) {
        next.disabled = activeSlide === slides.length - 1;
        next.setAttribute("aria-disabled", String(next.disabled));
      }
    };

    const settleGallery = () => {
      window.clearTimeout(gallerySettleTimer);
      gallerySettleTimer = 0;
      if (!track || galleryTouchActive) return;
      paintGallery(Math.round(track.scrollLeft / Math.max(track.clientWidth, 1)));
      commitGalleryRatio(activeSlide);
      requestedSlide = null;
    };
    const scheduleGallerySettle = () => {
      window.clearTimeout(gallerySettleTimer);
      // Fallback for browsers without scrollend; do not resize under a held touch.
      gallerySettleTimer = window.setTimeout(settleGallery, 180);
    };

    const goToSlide = index => {
      if (!track) return;
      const normalized = Math.max(0, Math.min(slides.length - 1, index));
      requestedSlide = normalized;
      warmGalleryImage(normalized);
      track.scrollTo({
        left: slides[normalized]?.offsetLeft ?? normalized * track.clientWidth,
        behavior: reducedMotion ? "auto" : "smooth"
      });
      if (reducedMotion) {
        window.clearTimeout(gallerySettleTimer);
        gallerySettleTimer = 0;
        paintGallery(normalized);
        commitGalleryRatio(normalized);
        requestedSlide = null;
      } else {
        scheduleGallerySettle();
      }
    };

    if (track && slides.length > 1) {
      const onGalleryScroll = () => {
        scheduleGallerySettle();
        if (galleryFrame) return;
        galleryFrame = window.requestAnimationFrame(() => {
          galleryFrame = 0;
          paintGallery(Math.round(track.scrollLeft / Math.max(track.clientWidth, 1)));
        });
      };
      const onGalleryKeydown = event => {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
        event.preventDefault();
        goToSlide((requestedSlide ?? activeSlide) + (event.key === "ArrowRight" ? 1 : -1));
      };
      const onPrevious = event => {
        goToSlide((requestedSlide ?? activeSlide) - 1);
        if (event.detail) event.currentTarget.blur();
      };
      const onNext = event => {
        goToSlide((requestedSlide ?? activeSlide) + 1);
        if (event.detail) event.currentTarget.blur();
      };
      const onPointerDown = () => {
        requestedSlide = null;
        warmGalleryImage(activeSlide + 1);
      };
      const onGalleryTouchStart = () => {
        galleryTouchActive = true;
        window.clearTimeout(gallerySettleTimer);
      };
      const onGalleryTouchEnd = event => {
        galleryTouchActive = event.touches.length > 0;
        if (!galleryTouchActive) scheduleGallerySettle();
      };

      track.addEventListener("scroll", onGalleryScroll, { passive: true });
      track.addEventListener("scrollend", settleGallery);
      track.addEventListener("touchstart", onGalleryTouchStart, { passive: true });
      track.addEventListener("touchend", onGalleryTouchEnd, { passive: true });
      track.addEventListener("touchcancel", onGalleryTouchEnd, { passive: true });
      track.addEventListener("keydown", onGalleryKeydown);
      track.addEventListener("pointerdown", onPointerDown, { passive: true });
      previous?.addEventListener("click", onPrevious);
      next?.addEventListener("click", onNext);
      paintGallery(0);

      cleanups.push(() => {
        track.removeEventListener("scroll", onGalleryScroll);
        track.removeEventListener("scrollend", settleGallery);
        track.removeEventListener("touchstart", onGalleryTouchStart);
        track.removeEventListener("touchend", onGalleryTouchEnd);
        track.removeEventListener("touchcancel", onGalleryTouchEnd);
        window.clearTimeout(gallerySettleTimer);
        track.removeEventListener("keydown", onGalleryKeydown);
        track.removeEventListener("pointerdown", onPointerDown);
        previous?.removeEventListener("click", onPrevious);
        next?.removeEventListener("click", onNext);
        window.cancelAnimationFrame(galleryFrame);
      });
    }

    paintGallery(0);
    commitGalleryRatio(0);

    const variantInputs = Array.from(root.querySelectorAll("[data-gdp-pdp-variant]"));
    const displayedPrice = root.querySelector("[data-gdp-pdp-price]");
    const displayedCompare = root.querySelector("[data-gdp-pdp-compare]");
    const mainButton = root.querySelector("[data-gdp-pdp-main-cta]");
    const mainLabel = root.querySelector("[data-gdp-pdp-main-label]");
    const mainPrice = root.querySelector("[data-gdp-pdp-main-price]");
    const closingPrice = root.querySelector("[data-gdp-pdp-closing-price]");
    const stickyPrice = root.querySelector("[data-gdp-pdp-sticky-price]");
    const stickyFormat = root.querySelector("[data-gdp-pdp-sticky-format]");
    const stickySubmit = root.querySelector("[data-gdp-pdp-sticky-submit]");
    const pair = root.querySelector("[data-gdp-pdp-pair]");
    const pairCurrent = root.querySelector("[data-gdp-pdp-pair-current]");
    const pairTotal = root.querySelector("[data-gdp-pdp-pair-total]");
    const pairSubmit = root.querySelector("[data-gdp-pdp-pair-submit]");
    const pairSubmitLabel = pairSubmit?.querySelector("[data-gdp-pdp-submit-label]");
    const initialPairLabel = pairSubmitLabel?.textContent?.trim() || "";
    const pairBaseLabel = pairSubmit?.dataset.baseLabel?.trim() || initialPairLabel;
    const pairUnavailableLabel = pairSubmit?.dataset.unavailableLabel?.trim() || initialPairLabel;
    const productForm = root.querySelector(".gdp-pdp-engine__product-form");
    const productFormInitiallyAvailable = Boolean(mainButton && !mainButton.disabled);
    const mainBaseLabel = mainButton?.dataset.baseLabel?.trim() || mainLabel?.textContent?.trim() || "";
    const mainChoiceLabel = mainButton?.dataset.choiceLabel?.trim() || mainBaseLabel;
    const mainUnavailableLabel = mainButton?.dataset.unavailableLabel?.trim() || mainBaseLabel;

    const getStickyLabel = input => {
      const explicitLabel = input?.dataset.stickyLabel?.trim();
      if (explicitLabel) return explicitLabel;
      const optionLabel = input?.closest(".gdp-pdp-engine__variant")
        ?.querySelector(".gdp-pdp-engine__variant-copy strong")
        ?.textContent
        ?.trim();
      if (optionLabel) return optionLabel;
      return root.querySelector(".gdp-pdp-engine__format-summary .gdp-pdp-engine__variant-copy strong")
        ?.textContent
        ?.trim() || stickyFormat?.textContent?.trim() || "";
    };

    const syncVariant = input => {
      if (!input) {
        const hasAvailableChoices = variantInputs.some(option => option.dataset.available === "true");
        const emptyChoiceLabel = hasAvailableChoices ? mainChoiceLabel : mainUnavailableLabel;
        if (mainButton) {
          mainButton.disabled = true;
          mainButton.setAttribute("aria-disabled", "true");
        }
        if (mainLabel) mainLabel.textContent = emptyChoiceLabel;
        if (stickyFormat) stickyFormat.textContent = emptyChoiceLabel;
        if (stickySubmit) {
          stickySubmit.disabled = true;
          stickySubmit.setAttribute("aria-disabled", "true");
        }
        if (pairSubmit) {
          pairSubmit.disabled = true;
          pairSubmit.setAttribute("aria-disabled", "true");
        }
        if (pairSubmitLabel) pairSubmitLabel.textContent = emptyChoiceLabel;
        if (mainPrice) mainPrice.hidden = true;
        if (displayedPrice) displayedPrice.hidden = true;
        if (closingPrice) closingPrice.hidden = true;
        if (stickyPrice) stickyPrice.hidden = true;
        return;
      }
      if (displayedPrice) displayedPrice.hidden = false;
      const catalogAvailable = input.dataset.available === "true";
      const submissionLocked = productForm?.dataset.gdpSubmitting === "true" ||
        productForm?.dataset.gdpPdpSubmitting === "true" || productForm?.dataset.gdpPdpNativeSubmitting === "true" ||
        window.GDPCart?.reviewRequired?.() === true;
      const pending = submissionLocked && productForm?.getAttribute("aria-busy") === "true";
      const available = catalogAvailable && !submissionLocked;
      const cartLabels = window.GDPCart?.labels || {};
      const priceText = input.dataset.priceText || "";
      const compareText = input.dataset.compareText || "";
      const ctaLabel = input.dataset.ctaLabel || mainBaseLabel;

      variantInputs.forEach(option => {
        const selected = option === input;
        option.checked = selected;
        option.closest(".gdp-pdp-engine__variant")?.classList.toggle("is-selected", selected);
      });

      if (displayedPrice) displayedPrice.textContent = priceText;
      const shipping = root.querySelector("[data-gdp-purchase-shipping]");
      const shippingLabel = shipping?.querySelector("[data-gdp-shipping-label]");
      const offerCents = Number(input.dataset.priceCents);
      if (shippingLabel && shipping.dataset.shippingEligible === "true" && Number.isFinite(offerCents)) {
        const homeThreshold = Number(shipping.dataset.homeThreshold);
        const reducedThreshold = Number(shipping.dataset.reducedThreshold);
        const relayThreshold = Number(shipping.dataset.relayThreshold);
        if (homeThreshold > 0 && reducedThreshold > 0 && relayThreshold > 0) {
          shippingLabel.textContent = offerCents >= homeThreshold ? shipping.dataset.homeShipping
            : offerCents >= reducedThreshold ? shipping.dataset.reducedShipping
            : offerCents >= relayThreshold ? shipping.dataset.relayShipping : shipping.dataset.standardShipping;
        }
      }
      if (displayedCompare) {
        displayedCompare.textContent = compareText;
        displayedCompare.hidden = !compareText;
      }
      if (mainButton) {
        mainButton.dataset.catalogAvailable = input.dataset.available;
        mainButton.disabled = !available;
        mainButton.setAttribute("aria-disabled", String(!available));
      }
      if (mainLabel) mainLabel.textContent = !catalogAvailable ? mainUnavailableLabel
        : pending ? (cartLabels.adding || ctaLabel)
        : submissionLocked ? (cartLabels.checkCart || ctaLabel) : ctaLabel;
      if (mainPrice) {
        mainPrice.textContent = priceText;
        mainPrice.hidden = !catalogAvailable;
      }
      if (closingPrice) { closingPrice.textContent = priceText; closingPrice.hidden = !priceText; }
      if (stickyPrice) { stickyPrice.textContent = priceText; stickyPrice.hidden = !priceText; }
      if (stickyFormat) stickyFormat.textContent = getStickyLabel(input);
      if (stickySubmit) {
        stickySubmit.disabled = !available;
        stickySubmit.setAttribute("aria-disabled", String(!available));
      }

      if (pairCurrent) pairCurrent.value = input.dataset.variantId || input.value;
      if (pair) pair.dataset.currentVariant = input.dataset.variantId || input.value;
      if (pairTotal && input.dataset.pairTotalText) pairTotal.textContent = input.dataset.pairTotalText;
      if (pairSubmit && pair) {
        const partnerAvailable = pair.dataset.partnerAvailable === "true";
        const pairAvailable = available && partnerAvailable && pair.dataset.catalogRejected !== "true";
        pairSubmit.disabled = !pairAvailable;
        pairSubmit.setAttribute("aria-disabled", String(!pairAvailable));
        if (pairSubmitLabel) {
          pairSubmitLabel.textContent = !catalogAvailable || !partnerAvailable || pair.dataset.catalogRejected === "true"
            ? pairUnavailableLabel
            : pending ? (cartLabels.adding || pairBaseLabel)
            : submissionLocked ? (cartLabels.checkCart || pairBaseLabel) : pairBaseLabel;
        }
      }
    };

    const onVariantChange = event => syncVariant(event.currentTarget);
    variantInputs.forEach(input => input.addEventListener("change", onVariantChange));
    if (variantInputs.length) {
      const tiersPending = variantInputs.some(input => input.dataset.tierLines) && !window.GDPTiers;
      syncVariant(tiersPending ? null : variantInputs.find(input => input.checked));
    }
    else if (mainButton && !root.querySelector(".gdp-legacy__purchase-adapter")) {
      if (closingPrice) closingPrice.hidden = false;
      if (stickyPrice) stickyPrice.hidden = false;
      if (stickySubmit) stickySubmit.disabled = mainButton.disabled;
    }
    const onCartFormState = event => {
      if (event.target !== productForm || !variantInputs.length) return;
      syncVariant(variantInputs.find(input => input.checked));
    };
    root.addEventListener("gdp:cart:form-state", onCartFormState);
    cleanups.push(() => root.removeEventListener("gdp:cart:form-state", onCartFormState));
    const onTierSync = event => syncVariant(event.target);
    const onTierUnselected = () => syncVariant(null);
    root.addEventListener("gdp:tier-sync", onTierSync);
    root.addEventListener("gdp:tier-unselected", onTierUnselected);
    cleanups.push(() => { root.removeEventListener("gdp:tier-sync", onTierSync); root.removeEventListener("gdp:tier-unselected", onTierUnselected); });
    cleanups.push(() => variantInputs.forEach(input => input.removeEventListener("change", onVariantChange)));

    const legacyPurchase = root.querySelector(".gdp-legacy__purchase-adapter");
    let legacyStickyObserver;
    if (legacyPurchase && stickyPrice && stickyFormat && stickySubmit && mainButton) {
      const syncLegacySticky = () => {
        const checkedInput = legacyPurchase.querySelector(
          "[data-gdp-legacy-option] input:checked, [data-gdp-legacy-format] input:checked"
        );
        const legacyLabel = checkedInput?.closest(".gdp-legacy__option")
          ?.querySelector(".gdp-legacy__option-copy strong")
          ?.textContent
          ?.trim();
        const legacyPrice = displayedPrice?.textContent?.trim();
        if (legacyLabel) stickyFormat.textContent = legacyLabel;
        if (legacyPrice) { stickyPrice.textContent = legacyPrice; stickyPrice.hidden = false; }
        stickySubmit.disabled = mainButton.disabled;
        stickySubmit.setAttribute("aria-disabled", String(mainButton.disabled));
      };
      legacyStickyObserver = new MutationObserver(syncLegacySticky);
      legacyStickyObserver.observe(legacyPurchase, {
        attributes: true,
        attributeFilter: ["class", "disabled"],
        childList: true,
        characterData: true,
        subtree: true
      });
      syncLegacySticky();
      cleanups.push(() => legacyStickyObserver.disconnect());
    }

    const restoreNativeProductForm = () => {
      if (!productForm) return;
      delete productForm.dataset.gdpPdpNativeSubmitting;
      productForm.removeAttribute("aria-busy");
      const selectedInput = variantInputs.find(input => input.checked);
      if (selectedInput || variantInputs.length) {
        syncVariant(selectedInput);
      } else {
        const available = productFormInitiallyAvailable;
        if (mainButton) mainButton.disabled = !available;
        if (stickySubmit) {
          stickySubmit.disabled = !available;
          stickySubmit.setAttribute("aria-disabled", String(!available));
        }
      }
    };

    const onNativeProductSubmit = event => {
      if (!productForm) return;
      if (mainButton?.disabled) { event.preventDefault(); return; }
      if (productForm.dataset.gdpPdpNativeSubmitting === "true") {
        event.preventDefault();
        return;
      }
      productForm.dataset.gdpPdpNativeSubmitting = "true";
      productForm.setAttribute("aria-busy", "true");
      if (mainButton) {
        mainButton.disabled = true;
        mainButton.setAttribute("aria-disabled", "true");
      }
      if (stickySubmit) {
        stickySubmit.disabled = true;
        stickySubmit.setAttribute("aria-disabled", "true");
      }
    };

    const onPageShow = event => {
      if (event.persisted) restoreNativeProductForm();
    };

    if (productForm) {
      productForm.addEventListener("submit", onNativeProductSubmit);
      window.addEventListener("pageshow", onPageShow);
      cleanups.push(() => {
        productForm.removeEventListener("submit", onNativeProductSubmit);
        window.removeEventListener("pageshow", onPageShow);
      });
    }

    const ajaxCartForms = Array.from(root.querySelectorAll("[data-gdp-pdp-ajax-cart]"));
    const canSubmitWithAjax = (
      typeof window.fetch === "function" &&
      typeof window.FormData === "function" &&
      typeof window.URL === "function" &&
      typeof window.CustomEvent === "function" &&
      typeof window.AbortController === "function"
    );
    const ajaxRestoreTimers = new Set();
    const ajaxRequestControllers = new Set();

    const getAjaxCartUrl = form => {
      // 03/09 : les formulaires secondaires (accord, cross-sell) portent leur destination
      // dans data-gdp-cart-action et un action neutre, pour que l'ecouteur Shopify
      // shop_events_listener (qui lit tous les <form action*="/cart/add"> au load et
      // impose le DERNIER comme "variante vue") ne remplace plus la variante du produit.
      const url = new URL(form.getAttribute("data-gdp-cart-action") || form.action, window.location.href);
      if (url.origin !== window.location.origin) return null;
      url.pathname = url.pathname.endsWith(".js")
        ? url.pathname
        : `${url.pathname.replace(/\/?$/, "")}.js`;
      return url.toString();
    };

    const onAjaxCartSubmit = async event => {
      const form = event.currentTarget;
      const bus = window.GDPCart;
      if (!bus) { event.preventDefault(); return; }
      event.preventDefault();
      const button = form.querySelector('button[type="submit"]');
      if (!button || button.disabled || form.dataset.gdpPdpSubmitting === "true") return;
      const label = button.querySelector("[data-gdp-pdp-submit-label]");
      const original = label?.textContent;
      const status = form.querySelector("[data-gdp-pdp-cart-status]");
      const cartLink = form.querySelector("[data-gdp-pdp-cart-link]");
      if (!getAjaxCartUrl(form)) { bus.showIssue(button, { mutationState: "not-sent" }); return; }
      form.dataset.gdpPdpSubmitting = "true"; form.setAttribute("aria-busy", "true");
      button.disabled = true;
      try {
        await bus.addFromForm(form);
        if (label) label.textContent = form.dataset.successLabel || original;
        if (status) status.textContent = form.dataset.successMessage || "";
        if (cartLink) cartLink.hidden = false;
        delete form.dataset.gdpPdpSubmitting;
        button.disabled = button.dataset.catalogAvailable === "false";
      } catch (error) {
        bus.showIssue(button, error);
        if (cartLink) { cartLink.hidden = false; cartLink.href = bus.root() + "cart?gdp_review=1"; }
      } finally {
        form.removeAttribute("aria-busy");
      }
    };
    ajaxCartForms.forEach(form => form.addEventListener("submit", onAjaxCartSubmit));
    cleanups.push(() => ajaxCartForms.forEach(form => form.removeEventListener("submit", onAjaxCartSubmit)));

    const relatedTrack = root.querySelector("[data-gdp-pdp-related-track]");
    const relatedCards = Array.from(root.querySelectorAll("[data-gdp-pdp-related-card]"));
    const relatedPager = root.querySelector("[data-gdp-pdp-related-pager]");
    const relatedDots = Array.from(root.querySelectorAll("[data-gdp-pdp-related-dot]"));
    let relatedFrame = 0;
    let paintedRelatedIndex = -1;
    let relatedResizePending = false;

    const paintRelatedPager = (activeIndex, forceCenter = false) => {
      if (paintedRelatedIndex === activeIndex && !forceCenter) return;
      paintedRelatedIndex = activeIndex;
      relatedDots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === activeIndex;
        dot.classList.toggle("is-active", isActive);
        dot.setAttribute("aria-current", String(isActive));
      });
      if (relatedPager?.classList.contains("is-scrollable")) {
        const activeDot = relatedDots[activeIndex];
        if (activeDot) {
          const centeredLeft = activeDot.offsetLeft - (relatedPager.clientWidth - activeDot.offsetWidth) / 2;
          relatedPager.scrollTo({
            left: Math.max(0, centeredLeft),
            behavior: reducedMotion ? "auto" : "smooth"
          });
        }
      }
    };

    const syncRelatedPager = () => {
      relatedFrame = 0;
      if (!relatedTrack || !relatedCards.length) return;
      const trackCenter = relatedTrack.getBoundingClientRect().left + relatedTrack.clientWidth / 2;
      let activeIndex = 0;
      let closestDistance = Infinity;

      relatedCards.forEach((card, cardIndex) => {
        const rect = card.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - trackCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          activeIndex = cardIndex;
        }
      });

      paintRelatedPager(activeIndex, relatedResizePending);
      relatedResizePending = false;
    };

    if (relatedTrack && relatedCards.length) {
      const onRelatedScroll = () => {
        if (!relatedFrame) relatedFrame = window.requestAnimationFrame(syncRelatedPager);
      };
      const onRelatedResize = () => {
        relatedResizePending = true;
        onRelatedScroll();
      };
      const relatedDotHandlers = new Map();

      relatedTrack.addEventListener("scroll", onRelatedScroll, { passive: true });
      window.addEventListener("resize", onRelatedResize);
      relatedDots.forEach((dot, dotIndex) => {
        const onRelatedDotClick = () => {
          const targetCard = relatedCards[dotIndex];
          if (!targetCard) return;
          const centeredLeft = targetCard.offsetLeft - (relatedTrack.clientWidth - targetCard.offsetWidth) / 2;
          relatedTrack.scrollTo({
            left: Math.max(0, centeredLeft),
            behavior: reducedMotion ? "auto" : "smooth",
          });
        };
        dot.addEventListener("click", onRelatedDotClick);
        relatedDotHandlers.set(dot, onRelatedDotClick);
      });
      paintRelatedPager(0);

      cleanups.push(() => {
        relatedTrack.removeEventListener("scroll", onRelatedScroll);
        window.removeEventListener("resize", onRelatedResize);
        relatedDotHandlers.forEach((handler, dot) => dot.removeEventListener("click", handler));
        relatedDotHandlers.clear();
        window.cancelAnimationFrame(relatedFrame);
      });
    }

    const liveReviewWidgets = Array.from(root.querySelectorAll(".custom-vstar-review-widget"));
    if (liveReviewWidgets.length) {
      const syncLiveReviewFallback = widget => {
        const hasReviewContent = Boolean(widget.children.length || widget.textContent.trim());
        if (hasReviewContent) widget.classList.remove("is-unavailable");
      };
      const liveReviewObserver = new MutationObserver(mutations => {
        mutations.forEach(mutation => syncLiveReviewFallback(mutation.target.closest?.(".custom-vstar-review-widget") || mutation.target));
      });
      liveReviewWidgets.forEach(widget => liveReviewObserver.observe(widget, { childList: true, subtree: true }));
      const reviewFallbackTimer = window.setTimeout(() => {
        liveReviewWidgets.forEach(widget => {
          if (!widget.children.length && !widget.textContent.trim()) widget.classList.add("is-unavailable");
        });
      }, 7000);
      cleanups.push(() => {
        liveReviewObserver.disconnect();
        window.clearTimeout(reviewFallbackTimer);
      });
    }

    const accordionGroups = [
      Array.from(root.querySelectorAll(".gdp-pdp-engine__faq details")),
      Array.from(root.querySelectorAll(".gdp-pdp-engine__food-recipes details"))
    ].filter(items => items.length);
    const accordionHandlers = new Map();
    accordionGroups.forEach(items => items.forEach(item => {
      const onAccordionToggle = event => {
        const current = event.currentTarget;
        if (!current.open) return;
        items.forEach(sibling => {
          if (sibling !== current) sibling.open = false;
        });
      };
      const singleFoodRecipe = items.length === 1 && item.classList.contains("gdp-pdp-engine__food-recipe");
      if (!singleFoodRecipe) item.removeAttribute("open");
      item.addEventListener("toggle", onAccordionToggle);
      accordionHandlers.set(item, onAccordionToggle);
    }));
    cleanups.push(() => {
      accordionHandlers.forEach((handler, item) => {
        item.removeEventListener("toggle", handler);
      });
      accordionHandlers.clear();
    });

    const revealElements = Array.from(root.querySelectorAll("[data-gdp-pdp-reveal]"));
    let revealObserver;
    if (!reducedMotion && "IntersectionObserver" in window) {
      root.classList.add("is-motion-ready");
      revealObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          entry.target.closest(".gdp-pdp-engine__ritual")?.classList.add("is-in");
          revealObserver.unobserve(entry.target);
        });
      }, { threshold: 0.08, rootMargin: "0px 0px -4% 0px" });
      revealElements.forEach(element => revealObserver.observe(element));
      cleanups.push(() => revealObserver.disconnect());
    } else {
      revealElements.forEach(element => element.classList.add("is-visible"));
    }

    const sticky = root.querySelector("[data-gdp-pdp-sticky]");
    const mainCta = root.querySelector("[data-gdp-pdp-main-cta]");
    const closing = root.querySelector("[data-gdp-pdp-closing]");
    const footer = Array.from(document.querySelectorAll(
      ".shopify-section-group-footer-group footer, footer[id*='__footer'], #shopify-section-footer footer, #shopify-section-footer, [data-section-type='footer']"
    )).find(candidate => !candidate.closest(ROOT_SELECTOR)) || null;
    const stickyCompetitors = Array.from(root.querySelectorAll("[data-gdp-pdp-sticky-competitor]"));
    // Une section éditoriale ne doit jamais faire disparaître l'action
    // d'achat pendant plusieurs écrans. Seuls les blocs commerce qui portent
    // explicitement cette valeur neutralisent le sticky ; closing et footer
    // sont traités séparément ci-dessous.
    const stickyBlockers = Array.from(root.querySelectorAll('[data-gdp-pdp-sticky-blocker="commerce"]'));
    let stickyFrame = 0;
    let stickyVisible = null;

    const setStickyVisible = visible => {
      if (stickyVisible === visible) return;
      stickyVisible = visible;
      sticky.classList.toggle("is-visible", visible);
      sticky.setAttribute("aria-hidden", String(!visible));
      if (stickySubmit) stickySubmit.tabIndex = visible ? 0 : -1;
    };

    const measureSticky = () => {
      stickyFrame = 0;
      if (!sticky || !mainCta) return;
      const ctaPassed = mainCta.getBoundingClientRect().bottom < 0;
      if (!ctaPassed) {
        setStickyVisible(false);
        return;
      }
      const closingRect = closing?.getBoundingClientRect();
      const closingVisible = Boolean(
        closingRect &&
        closingRect.top < window.innerHeight &&
        closingRect.bottom > 0
      );
      const footerRect = footer?.getBoundingClientRect();
      const footerVisible = Boolean(
        footerRect &&
        footerRect.top < window.innerHeight &&
        footerRect.bottom > 0
      );
      const blockerVisible = stickyBlockers.some(blocker => {
        const rect = blocker.getBoundingClientRect();
        return rect.top < window.innerHeight && rect.bottom > 0;
      });
      const stickyRect = sticky.getBoundingClientRect();
      const stickyBottom = Number.parseFloat(window.getComputedStyle(sticky).bottom) || 0;
      const stickyNaturalBottom = window.innerHeight - stickyBottom;
      const stickyNaturalTop = stickyNaturalBottom - sticky.offsetHeight;
      const competitorOverlap = stickyCompetitors.some(competitor => {
        const rect = competitor.getBoundingClientRect();
        return (
          rect.bottom > stickyNaturalTop &&
          rect.top < stickyNaturalBottom &&
          rect.right > stickyRect.left &&
          rect.left < stickyRect.right
        );
      });
      const visible = ctaPassed && !closingVisible && !footerVisible && !blockerVisible && !competitorOverlap;
      setStickyVisible(visible);
    };

    const queueStickyMeasure = () => {
      if (!stickyFrame) stickyFrame = window.requestAnimationFrame(measureSticky);
    };

    if (sticky && mainCta) {
      const stickyLayoutDetails = Array.from(root.querySelectorAll("details"));
      window.addEventListener("scroll", queueStickyMeasure, { passive: true });
      window.addEventListener("resize", queueStickyMeasure);
      stickyLayoutDetails.forEach(details => details.addEventListener("toggle", queueStickyMeasure));
      queueStickyMeasure();
      cleanups.push(() => {
        window.removeEventListener("scroll", queueStickyMeasure);
        window.removeEventListener("resize", queueStickyMeasure);
        stickyLayoutDetails.forEach(details => details.removeEventListener("toggle", queueStickyMeasure));
        window.cancelAnimationFrame(stickyFrame);
      });
    }

    root.gdpPdpCleanup = () => {
      cleanups.forEach(cleanup => cleanup());
      delete root.gdpPdpCleanup;
      delete root.dataset.gdpPdpReady;
    };
  };

  const init = (scope = document) => collectRoots(scope).forEach(initRoot);

  if (!window.GDPPDPEngine) {
    window.GDPPDPEngine = { init };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => init(document), { once: true });
    } else {
      init(document);
    }

    document.addEventListener("shopify:section:load", event => init(event.target));
    document.addEventListener("shopify:section:unload", event => {
      collectRoots(event.target).forEach(root => root.gdpPdpCleanup?.());
    });
  } else {
    window.GDPPDPEngine.init(document);
  }
})();
